import { useState } from 'react';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { readStreamProbe, VERDICT_TONE } from '../utils/streamCheck';
import { toneFor, channelRow, sourceNote, missingSettings, disabledChannels, TEST_SEND_TIMEOUT_MS, queueTone, queueRowLine } from '../utils/notificationTest';
import { useApi } from '../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Badge, Tabs, useToast, PageHeader, FieldLabel, Icon } from '../components/ui';
import { Users } from './Users';
import { SageImport } from './SageImport';
import { RateIndexes } from './RateIndexes';
import { MoveDeals } from './MoveDeals';
import { AIConnections } from './AIConnections';
import { CompanyEntities } from './CompanyEntities';
import { GovWinEmails } from './GovWinEmails';
import { ReorganizeDocuments } from './ReorganizeDocuments';
import { ShareFileSettings } from './ShareFileSettings';
import { NoteTaskBackfillPanel } from '../components/shared/NoteTaskBackfillPanel';
import { AppDownloadsPanel } from '../components/shared/AppDownloadsPanel';
import { useUser } from '../context/UserContext';
import { formatInstant } from '../utils/format';

// App Downloads is Neil's switch for the public iOS download pages; the API
// enforces the same (functions/appDownloads.js), this only hides the tab.
const APP_DOWNLOADS_OWNER = 'ngodfrey@harvus.com';

const TABS = ['Users', 'Sage Import', 'Rate Indexes', 'Move Deals', 'Documents', 'ShareFile', 'Company Entities', 'Payment Generation', 'Integrations', 'Notifications', 'AI Connections', 'Connection Tests', 'GovWin'];

// Small inline status text with a leading icon — replaces the old ✓/⚠/✗ glyphs.
const StatusText = ({ kind, color, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color }}>
    <Icon name={kind} size={14} /> {children}
  </span>
);

// ── Payment Generation tab ──────────────────────────────────────────────────
const PaymentGenerationTab = () => {
  const { toast } = useToast();
  const { data: settings, loading, error, reload } = useApi(() => api.get("settings"), []);
  const [saving, setSaving]       = useState(false);
  const [running, setRunning]     = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [migrRunning, setMigrRunning] = useState(false);
  const [migrResult, setMigrResult]   = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult]   = useState(null);

  const toggleSetting = async (key, currentVal) => {
    setSaving(true);
    try {
      await api.put("settings", { [key]: currentVal === "true" ? "false" : "true" });
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const runPayments = async () => {
    setRunning(true); setRunResult(null);
    try {
      const result = await api.post("payments/generate", {});
      setRunResult(`Generated ${result.generated} payment(s), skipped ${result.skipped} existing.`);
      reload();
    } catch (e) { setRunResult(`Error: ${e.message}`); }
    finally { setRunning(false); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const payEnabled = settings?.paymentGenerationEnabled === "true";

  return (
    <div>
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Payment Generation</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Controls automatic daily payment schedule generation (runs at 07:00 Central). When disabled, the timer still runs but skips processing. Use the manual trigger to generate payments on demand.
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: `1px solid ${B.border}` }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Auto Payment Generation</div>
            <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>Daily timer at 07:00 Central</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Badge color={payEnabled ? B.green : B.textLt}>{payEnabled ? "Enabled" : "Disabled"}</Badge>
            <Btn size="sm" variant={payEnabled ? "danger" : "secondary"} disabled={saving}
              onClick={() => toggleSetting("paymentGenerationEnabled", settings.paymentGenerationEnabled)}>
              {payEnabled ? "Disable" : "Enable"}
            </Btn>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Manual Trigger</div>
            <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
              Immediately generate payment records for all active loans with a rate schedule.
              {!payEnabled && <span style={{ color: B.amber }}> (Auto generation is currently disabled)</span>}
            </div>
          </div>
          <Btn size="sm" variant="secondary" disabled={running || !payEnabled} onClick={runPayments}>
            {running ? "Running…" : <><Icon name="play" size={14} /> Run Now</>}
          </Btn>
        </div>

        {runResult && (
          <div style={{ marginTop: 8, padding: "10px 14px", background: B.surfaceMuted, borderRadius: 6, fontSize: 13, color: B.text }}>
            {runResult}
          </div>
        )}
      </Card>

      {/* Database Migrations */}
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Database Migrations</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Applies any pending schema changes (idempotent — safe to run multiple times).
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Btn size="sm" variant="secondary" disabled={migrRunning} onClick={async () => {
            setMigrRunning(true); setMigrResult(null);
            try {
              const res = await api.post('admin/run-migrations', {});
              setMigrResult(res);
            } catch (e) { setMigrResult({ errors: 1, results: [{ label: 'request', status: 'error', detail: e.message }] }); }
            finally { setMigrRunning(false); }
          }}>
            {migrRunning ? 'Running…' : <><Icon name="play" size={14} /> Run Migrations</>}
          </Btn>
          {migrResult && (
            <StatusText kind={migrResult.errors ? 'warning' : 'check'} color={migrResult.errors ? B.red : B.green}>
              {migrResult.errors ? `${migrResult.errors} error(s)` : `${migrResult.applied} step(s) applied`}
            </StatusText>
          )}
        </div>
        {migrResult?.results && (
          <div style={{ marginTop: 12, fontSize: 12, fontFamily: 'monospace', background: B.border, borderRadius: 4, padding: '8px 12px', maxHeight: 200, overflowY: 'auto' }}>
            {migrResult.results.map((r, i) => (
              <div key={i} style={{ color: r.status === 'error' ? B.red : B.textMd, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icon name={r.status === 'error' ? 'close' : 'check'} size={12} /> {r.label}{r.detail ? `: ${r.detail}` : ''}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Note Summaries */}
      <Card style={{ marginTop: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Note Summaries</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Queues a Claude summary rebuild for every deal, deal detail, and task that has notes, so existing notes are
          summarized without waiting for someone to open each tab. New notes are summarized automatically as they're
          added or edited. Runs in the background — safe to run multiple times.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Btn size="sm" variant="secondary" disabled={backfillRunning} onClick={async () => {
            setBackfillRunning(true); setBackfillResult(null);
            try {
              const res = await api.post('notes/summaries/backfill', {});
              setBackfillResult({ ok: true, ...res });
            } catch (e) { setBackfillResult({ ok: false, error: e.message }); }
            finally { setBackfillRunning(false); }
          }}>
            {backfillRunning ? 'Queuing…' : '▶ Rebuild All Summaries'}
          </Btn>
          {backfillResult && (
            <span style={{ fontSize: 13, color: backfillResult.ok && backfillResult.storage !== false ? B.green : B.red }}>
              {!backfillResult.ok
                ? `⚠ ${backfillResult.error}`
                : backfillResult.storage === false
                  ? '⚠ No storage connection — nothing queued'
                  : `✓ Queued ${backfillResult.enqueued} of ${backfillResult.total} scope(s)${backfillResult.failed ? `, ${backfillResult.failed} failed` : ''}. Summaries generate in the background.`}
            </span>
          )}
        </div>
      </Card>

      <NoteTaskBackfillPanel />
    </div>
  );
};

// ── Notifications tab ───────────────────────────────────────────────────────
const NotificationsTab = () => {
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult]   = useState(null);

  const runNotifTest = async () => {
    setTestSending(true); setTestResult(null);
    try {
      const body = {};
      if (testEmail.trim()) body.email = testEmail.trim();
      if (testPhone.trim()) body.phone = testPhone.trim();
      // Long budget on purpose — the route sends for real and deadlines each
      // channel at 12s, so the 10s default cannot fit a working send.
      const r = await api.post('notifications/test-send', body, { timeout: TEST_SEND_TIMEOUT_MS });
      setTestResult({ ok: true, data: r });
    } catch (e) { setTestResult({ ok: false, error: e.message }); }
    finally { setTestSending(false); }
  };

  return (
    <>
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Notification Test</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Sends a real test email, SMS and Teams DM, then says which of the four things is true:
          it sent, this environment discards everything (dry-run), Azure Communication Services
          is not configured, or it is configured and refused. Leave the fields blank to use your
          own account email and phone.
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <FieldLabel>Email override</FieldLabel>
            <input
              value={testEmail} onChange={e => setTestEmail(e.target.value)}
              placeholder="Leave blank to use your account email"
              style={{ width: '100%', border: `1px solid ${B.border}`, borderRadius: 6, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <FieldLabel>Phone override</FieldLabel>
            <input
              type="tel"
              value={testPhone} onChange={e => setTestPhone(e.target.value)}
              placeholder="+15555551234"
              style={{ width: '100%', border: `1px solid ${B.border}`, borderRadius: 6, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: B.surfaceMuted, border: `1px solid ${B.border}`, fontSize: 12, color: B.textMd }}>
          <strong>Teams DM</strong> — will be sent to your registered bot conversation if you have run <span style={{ fontFamily: 'monospace', background: B.border, padding: '1px 5px', borderRadius: 3 }}>register</span> in Teams.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Btn size="sm" disabled={testSending} onClick={runNotifTest}>
            {testSending ? 'Sending…' : <><Icon name="play" size={14} /> Send Test</>}
          </Btn>
          {testResult && !testResult.ok && (
            <StatusText kind="warning" color={B.red}>{testResult.error}</StatusText>
          )}
        </div>
        {testResult?.ok && <NotifTestResult data={testResult.data} />}
      </Card>
      <NotificationQueuesCard />
    </>
  );
};

// ── The queue leg ───────────────────────────────────────────────────────────
//
// The test above sends and tells you whether the TRANSPORT works. Most
// notifications never reach the transport: a task assigned, and a note /
// document / task / email arriving on a deal, are ENQUEUED and delivered later
// by a worker, because the write they hang off is already committed and must
// not wait on an email poll. Every failure on that leg is best-effort by design
// and swallowed — so a green send test above a broken queue is exactly what
// somebody sees while their notifications quietly go nowhere.
//
// It reads the queues; it sends nothing and dequeues nothing, so it is safe to
// press. The verdict is rendered VERBATIM from the API for the same reason the
// one above it is: a second copy of the diagnosis is free to disagree with the
// first.
const NotificationQueuesCard = () => {
  const { data, loading, error, reload } = useApi(() => api.get('notifications/queues'), []);

  const tone = queueTone(data?.verdict?.kind);
  const color = TONE_COLORS[tone] || B.amber;

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text }}>Notification queues</div>
        <Btn size="sm" variant="ghost" disabled={loading} onClick={reload}>
          <Icon name="refresh" size={14} /> {loading ? 'Checking…' : 'Refresh'}
        </Btn>
      </div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
        A task assigned, or a note, document, task or email arriving on a deal, is queued and
        delivered by a background worker — it does not go straight out like the test above.
        This reads those queues, so &ldquo;never enqueued&rdquo;, &ldquo;enqueued and nothing is
        draining it&rdquo; and &ldquo;the worker is failing on every message&rdquo; can be told
        apart. Nothing is sent and nothing is removed.
      </div>

      {loading && <Spinner />}
      {error && <ErrorMsg msg={error} onRetry={reload} />}

      {data && (
        <div style={{ padding: '12px 14px', borderRadius: 6, background: color + '14', border: `1px solid ${color}44`, fontSize: 13 }}>
          <div style={{ color, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon name={tone === 'good' ? 'check' : 'warning'} size={14} />
            {data?.verdict?.message || 'The queues were read but reported no verdict.'}
          </div>

          {(data.queues || []).map(q => {
            const row = queueRowLine(q);
            return (
              <div key={q.name} style={{ padding: '5px 0' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong>{q.label || q.name}</strong>
                  <span style={{ color: TONE_COLORS[row.tone] || B.textMd }}>— {row.label}</span>
                </div>
                {/* What actually rides on the queue, so a red row says whose
                    notifications stopped rather than naming a queue nobody has
                    heard of. */}
                {q.carries && <div style={{ fontSize: 12, color: B.textLt }}>{q.carries}</div>}
              </div>
            );
          })}

          {data.storageConfigured === false && (
            <div style={{ marginTop: 8, fontSize: 12, color: B.red }}>
              Set one of: {(data.settings || []).map(m => (
                <span key={m} style={{ fontFamily: 'monospace', background: B.surfaceMuted, padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{m}</span>
              ))}
            </div>
          )}

          {data.checkedUTC && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${B.border}`, fontSize: 12, color: B.textLt }}>
              Counts are Azure&rsquo;s approximate depths and exclude a message being processed
              right now — a small number during a burst is normal. Checked {formatInstant(data.checkedUTC, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}.
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// The result of a notification self-test.
//
// It leads with the SERVER'S verdict rather than with the channel rows, because
// the rows on their own cannot answer the question people bring here. "Email:
// not sent" has four causes that look identical from a seat — the environment
// is in dry-run, ACS is unconfigured, ACS refused, or the recipient has email
// switched off — and the verdict names which. The rows are the evidence
// underneath it.
const TONE_COLORS = { good: B.green, bad: B.red, warn: B.amber, muted: B.textLt };

const ChannelLine = ({ label, target, targetSource, result }) => {
  const row = channelRow(result);
  const note = sourceNote(targetSource);
  return (
    <div style={{ display: 'flex', gap: 8, padding: '4px 0', alignItems: 'baseline', flexWrap: 'wrap' }}>
      <strong style={{ minWidth: 76 }}>{label}</strong>
      <span style={{ color: B.textMd }}>
        {target || '(no target)'}{note && <span style={{ color: B.textLt }}> ({note})</span>}
      </span>
      <span style={{ color: TONE_COLORS[row.tone] || B.textMd }}>— {row.label}</span>
    </div>
  );
};

const NotifTestResult = ({ data: d }) => {
  const tone = toneFor(d?.verdict?.kind);
  const color = TONE_COLORS[tone] || B.amber;
  const missing = missingSettings(d?.config);
  const offChannels = disabledChannels(d?.prefs);

  return (
    <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 6, background: color + '14', border: `1px solid ${color}44`, fontSize: 13 }}>
      {/* The verdict is rendered verbatim from the API — inventing a message
          here would be a second copy of the diagnosis, free to disagree. */}
      <div style={{ color, fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
        <Icon name={tone === 'good' ? 'check' : 'warning'} size={14} />
        {d?.verdict?.message || 'The test ran but reported no verdict.'}
      </div>

      <ChannelLine label="Email" target={d?.toEmail} targetSource={d?.emailSource} result={d?.email} />
      <ChannelLine label="SMS" target={d?.toPhone} targetSource={d?.phoneSource} result={d?.sms} />
      <ChannelLine label="Teams DM" target={d?.requestedBy} result={d?.teams} />

      <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${B.border}`, fontSize: 12, color: B.textLt }}>
        Mode <strong style={{ color: B.textMd }}>{d?.mode}</strong>
        {' · '}From {d?.fromEmail || '(no sender configured)'}
        {d?.fromPhone ? ` / ${d.fromPhone}` : ''}
      </div>

      {/* Which app settings to set, by name. "Email is not configured" sends
          somebody hunting; a variable name is a fix. */}
      {missing.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: B.red }}>
          Missing app settings: {missing.map(m => (
            <span key={m} style={{ fontFamily: 'monospace', background: B.surfaceMuted, padding: '1px 5px', borderRadius: 3, marginRight: 4 }}>{m}</span>
          ))}
        </div>
      )}

      {/* Reported separately from the sends, because a green transport into a
          switched-off channel still reaches nobody. */}
      {offChannels.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: B.amber }}>
          Your account has {offChannels.join(', ')} notifications turned off — real notifications
          on {offChannels.length === 1 ? 'that channel' : 'those channels'} are suppressed for you
          even though the test above sent.
        </div>
      )}
    </div>
  );
};

// ── Connection Tests tab (SharePoint + Claude API) ──────────────────────────
// Whether server-sent events actually reach this browser, or something in
// front of the API holds them and delivers them together at the end. The server
// cannot tell — it writes frames on the same schedule either way — so the test
// has to run from here and time their arrival.
const StreamingCheckCard = () => {
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    const startedAt = Date.now();
    const arrivals = [];
    let expected = 0;
    try {
      const { streamed, data } = await api.postStream('diagnostics/stream-check', {}, {
        onEvent: (evt) => {
          if (evt.event === 'probe-start') expected = evt.data?.expectedSpreadMs || 0;
          else if (evt.event === 'probe')  arrivals.push(Date.now() - startedAt);
        },
      });
      // The endpoint answers in JSON when streaming is switched off server-side
      // — which is a different problem from a buffering proxy, so say which.
      if (!streamed && data && data.available === false) {
        setResult({ verdict: 'not-streamed', headline: 'Streaming is off for this environment',
                    detail: data.reason, spreadMs: 0, firstFrameMs: null });
        return;
      }
      setResult(readStreamProbe({ arrivals, expectedSpreadMs: expected, streamed }));
    } catch (e) {
      setResult({ verdict: 'error', headline: 'The test could not run', detail: e.message });
    } finally {
      setRunning(false);
    }
  };

  const tone = result ? (VERDICT_TONE[result.verdict] || 'warn') : null;
  const toneColor = tone === 'good' ? B.green : tone === 'bad' ? B.red : B.amber;

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Streaming (live chat answers)</div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
        Checks whether server-sent events reach this browser one at a time, or arrive together at the end.
        The Harvi chat can only stream answers word-by-word if they come through live — if something in front
        of the API buffers them, chat still works, it just waits for the whole answer first.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Btn size="sm" variant="secondary" disabled={running} onClick={run}>
          {running ? 'Testing…' : <><Icon name="play" size={14} /> Test Streaming</>}
        </Btn>
        {result && (
          <StatusText kind={tone === 'good' ? 'check' : 'warning'} color={toneColor}>{result.headline}</StatusText>
        )}
      </div>
      {result?.detail && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: B.surfaceMuted, borderRadius: 6, fontSize: 13, color: B.textMd }}>
          {result.detail}
          {result.firstFrameMs != null && (
            <div style={{ marginTop: 6, fontSize: 12, color: B.textLt }}>
              First frame after {Math.round(result.firstFrameMs)}ms · frames spread over {Math.round(result.spreadMs)}ms
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const ConnectionTestsTab = () => {
  const [spFolderRunning, setSpFolderRunning] = useState(false);
  const [spFolderResult, setSpFolderResult]   = useState(null);
  const [claudeChecking, setClaudeChecking]   = useState(false);
  const [claudeResult, setClaudeResult]       = useState(null);

  return (
    <div>
      {/* SharePoint Deal Folders */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>SharePoint — Deal Folders</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Ensures every deal has a corresponding folder in SharePoint. Safe to run at any time — folders that already exist are skipped.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: spFolderResult ? 12 : 0 }}>
          <Btn size="sm" variant="secondary" disabled={spFolderRunning} onClick={async () => {
            setSpFolderRunning(true); setSpFolderResult(null);
            try {
              const res = await api.post('sharepoint/ensure-deal-folders', {});
              setSpFolderResult({ ok: true, data: res });
            } catch (e) { setSpFolderResult({ ok: false, error: e.message }); }
            finally { setSpFolderRunning(false); }
          }}>
            {spFolderRunning ? 'Checking…' : <><Icon name="play" size={14} /> Check & Create Deal Folders</>}
          </Btn>
          {spFolderResult && !spFolderResult.ok && (
            <StatusText kind="warning" color={B.red}>{spFolderResult.error}</StatusText>
          )}
        </div>
        {spFolderResult?.ok && (() => {
          const { data } = spFolderResult;
          const errors = (data.results || []).filter(r => r.status === 'error');
          return (
            <div style={{ marginTop: 8, padding: '10px 14px', background: errors.length ? B.amber + '14' : B.surfaceMuted, borderRadius: 6, fontSize: 13 }}>
              <div style={{ marginBottom: errors.length ? 8 : 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icon name="check" size={13} /> {data.created} folder{data.created !== 1 ? 's' : ''} confirmed{data.errors > 0 ? `, ${data.errors} error${data.errors !== 1 ? 's' : ''}` : ''}.
              </div>
              {errors.map(r => (
                <div key={r.dealId} style={{ color: B.red, marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon name="close" size={13} /> {r.name}: {r.error}
                </div>
              ))}
            </div>
          );
        })()}
      </Card>

      {/* Streaming (SSE) reachability */}
      <StreamingCheckCard />

      {/* Claude API Connection */}
      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Claude API Connection</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Verifies that the <code style={{ fontFamily: 'monospace', background: B.border, padding: '1px 5px', borderRadius: 3 }}>ANTHROPIC_API_KEY</code> is configured and reachable. Used by the Teams and SMS AI features.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Btn size="sm" variant="secondary" disabled={claudeChecking} onClick={async () => {
            setClaudeChecking(true); setClaudeResult(null);
            try {
              const res = await api.post('settings/test-claude', {});
              setClaudeResult(res);
            } catch (e) { setClaudeResult({ ok: false, error: e.message }); }
            finally { setClaudeChecking(false); }
          }}>
            {claudeChecking ? 'Checking…' : <><Icon name="play" size={14} /> Test Connection</>}
          </Btn>
          {claudeResult && !claudeResult.ok && (
            <StatusText kind="warning" color={B.red}>{claudeResult.error}</StatusText>
          )}
          {claudeResult?.ok && (
            <StatusText kind="check" color={B.green}>Connected</StatusText>
          )}
        </div>
      </Card>
    </div>
  );
};

// ── Integrations tab ────────────────────────────────────────────────────────
// The per-user connect cards (Mail & Calendar, Microsoft To Do) used to live
// here — they moved to pages/UserSettings.jsx ("My Settings" in the sidebar,
// reachable to every user, not just Admins) since connecting YOUR OWN
// Microsoft account is a self-service action, not an admin one. This tab now
// holds only the org-wide switch: whether the background sync timer runs at
// all for anybody.
const IntegrationsTab = ({ setPage }) => {
  const { toast } = useToast();
  const { data: settings, loading, error, reload } = useApi(() => api.get("settings"), []);
  const [saving, setSaving] = useState(false);

  const toggleSetting = async (key, currentVal) => {
    setSaving(true);
    try {
      await api.put("settings", { [key]: currentVal === "true" ? "false" : "true" });
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  const todoEnabled = settings?.todoSyncEnabled === "true";

  return (
    <div>
      <Card style={{ marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: B.textMd }}>
          Connecting your own Outlook mail &amp; calendar or Microsoft To Do is a personal
          setting now, on <strong>My Settings</strong> — everyone reaches it from the sidebar.
        </div>
        {setPage && (
          <Btn size="sm" variant="secondary" onClick={() => setPage("userSettings")}>Go to My Settings</Btn>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 15, fontWeight: 700, color: B.text, marginBottom: 4 }}>Microsoft To Do Sync</div>
        <div style={{ fontSize: 13, color: B.textMd, marginBottom: 16 }}>
          Global switch for two-way task sync with each connected user's Microsoft To Do
          (background timer runs every 15 min). When disabled, the timer still runs but
          skips processing — individual users' connections are preserved. Users connect
          their own account from My Settings.
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Auto To Do Sync</div>
            <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>Reconciliation timer every 15 minutes</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Badge color={todoEnabled ? B.green : B.textLt}>{todoEnabled ? "Enabled" : "Disabled"}</Badge>
            <Btn size="sm" variant={todoEnabled ? "danger" : "secondary"} disabled={saving}
              onClick={() => toggleSetting("todoSyncEnabled", settings.todoSyncEnabled)}>
              {todoEnabled ? "Disable" : "Enable"}
            </Btn>
          </div>
        </div>
      </Card>
    </div>
  );
};

export const Settings = ({ setPage }) => {
  const user = useUser();
  const isOwner = ((user && (user.email || user.Email)) || '').trim().toLowerCase() === APP_DOWNLOADS_OWNER;
  const tabs = isOwner ? [...TABS, 'App Downloads'] : TABS;
  const [tab, setTab] = useState(TABS[0]);

  return (
    <div>
      <PageHeader icon="settings" title="Settings" />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'Users'              && <Users />}
      {tab === 'Sage Import'        && <SageImport />}
      {tab === 'Rate Indexes'       && <RateIndexes />}
      {tab === 'Move Deals'         && <MoveDeals />}
      {tab === 'Documents'          && <ReorganizeDocuments />}
      {tab === 'ShareFile'          && <ShareFileSettings />}
      {tab === 'Company Entities'   && <CompanyEntities />}
      {tab === 'Payment Generation' && <PaymentGenerationTab />}
      {tab === 'Integrations'       && <IntegrationsTab setPage={setPage} />}
      {tab === 'Notifications'      && <NotificationsTab />}
      {tab === 'AI Connections'     && <AIConnections />}
      {tab === 'Connection Tests'   && <ConnectionTestsTab />}
      {tab === 'GovWin'            && <GovWinEmails />}
      {tab === 'App Downloads' && isOwner && <AppDownloadsPanel />}
    </div>
  );
};
